<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignsAPI from 'dashboard/api/campaigns';

const props = defineProps({
  selectedCampaign: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();

const dialogRef = ref(null);
const isLoading = ref(false);
const stats = ref(null);
const failures = ref([]);

const campaignId = computed(() => props.selectedCampaign?.id);

const fetchStats = async () => {
  if (!campaignId.value) return;
  isLoading.value = true;
  try {
    const response = await CampaignsAPI.whatsappStats(campaignId.value);
    stats.value = response.data?.stats || null;
    failures.value = response.data?.failures || [];
  } catch (e) {
    stats.value = null;
    failures.value = [];
  } finally {
    isLoading.value = false;
  }
};

const handleClose = () => {
  dialogRef.value?.close();
};

watch(
  () => props.selectedCampaign,
  next => {
    if (!next) return;
    // When dialog is opened, the parent sets selectedCampaign; fetch fresh stats.
    fetchStats();
  }
);

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    :title="t('CAMPAIGN.WHATSAPP.RESULTS.TITLE')"
    :confirm-button-label="t('CAMPAIGN.WHATSAPP.RESULTS.CLOSE')"
    :show-cancel-button="false"
    overflow-y-auto
    @confirm="handleClose"
  >
    <div v-if="isLoading" class="flex items-center justify-center py-10">
      <Spinner />
    </div>

    <template v-else>
      <div v-if="!stats" class="text-sm text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.RESULTS.EMPTY') }}
      </div>

      <div v-else class="grid grid-cols-2 gap-3">
        <div class="p-3 rounded-lg bg-n-alpha-2">
          <div class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.RESULTS.SUMMARY.TOTAL') }}
          </div>
          <div class="text-lg font-medium text-n-slate-12">
            {{ stats.total }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-2">
          <div class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.RESULTS.SUMMARY.SENT') }}
          </div>
          <div class="text-lg font-medium text-n-slate-12">
            {{ stats.sent }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-2">
          <div class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.RESULTS.SUMMARY.FAILED') }}
          </div>
          <div class="text-lg font-medium text-n-slate-12">
            {{ stats.failed }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-2">
          <div class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.RESULTS.SUMMARY.QUEUED') }}
          </div>
          <div class="text-lg font-medium text-n-slate-12">
            {{ stats.queued }}
          </div>
        </div>
      </div>

      <div class="mt-6">
        <div class="text-sm font-medium text-n-slate-12 mb-2">
          {{ t('CAMPAIGN.WHATSAPP.RESULTS.FAILURES.TITLE') }}
        </div>

        <div v-if="failures.length === 0" class="text-sm text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.RESULTS.FAILURES.EMPTY') }}
        </div>

        <div v-else class="border rounded-lg border-n-weak overflow-hidden">
          <div
            class="grid grid-cols-12 gap-2 px-3 py-2 bg-n-alpha-2 text-xs text-n-slate-11"
          >
            <div class="col-span-4">
              {{ t('CAMPAIGN.WHATSAPP.RESULTS.FAILURES.TABLE.WA_ID') }}
            </div>
            <div class="col-span-2">
              {{ t('CAMPAIGN.WHATSAPP.RESULTS.FAILURES.TABLE.CODE') }}
            </div>
            <div class="col-span-6">
              {{ t('CAMPAIGN.WHATSAPP.RESULTS.FAILURES.TABLE.MESSAGE') }}
            </div>
          </div>
          <div
            v-for="(row, index) in failures"
            :key="`${row.wa_id}-${index}`"
            class="grid grid-cols-12 gap-2 px-3 py-2 text-sm border-t border-n-weak"
          >
            <div class="col-span-4 font-mono text-n-slate-12 truncate">
              {{ row.wa_id }}
            </div>
            <div class="col-span-2 text-n-slate-11">
              {{ row.error_code || '-' }}
            </div>
            <div class="col-span-6 text-n-slate-11 line-clamp-2">
              {{ row.error_message || '-' }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </Dialog>
</template>
